import { Injectable, inject } from '@angular/core';
import { APP_CONFIG } from '@cardapio-online/config';
import { SnackbarService } from '@cardapio-online/core/snackbar/snackbar';
import {
  fetchEventSource,
  type EventSourceMessage,
} from '@microsoft/fetch-event-source';
import { NotificationSettingsService } from '../data-access/notification-settings.service';
import type { NotificationRealtimeEvent } from '../models/notification-event.model';
import { NotificationStore } from '../stores/notification.store';
import { NotificationRulesService } from './notification-rules.service';

@Injectable({
  providedIn: 'root',
})
export class NotificationDispatcherService {
  private readonly cfg = inject(APP_CONFIG);
  private readonly snackbarService = inject(SnackbarService);
  private readonly notificationStore = inject(NotificationStore);
  private readonly notificationRulesService = inject(NotificationRulesService);
  private readonly notificationSettingsService = inject(NotificationSettingsService);

  private started = false;

  start(): void {
    if (this.started || typeof window === 'undefined') {
      return;
    }

    this.started = true;
    this.notificationStore.setConnectionState('connecting');

    this.notificationSettingsService.load().subscribe({
      next: () => this.connectToStream(),
      error: () => this.connectToStream(),
    });
  }

  private connectToStream(): void {
    const tenantSlug = this.resolveTenantSlug();

    if (!tenantSlug) {
      this.started = false;
      this.notificationStore.setConnectionState('error');
      return;
    }

    const headers: Record<string, string> = {
      Accept: 'text/event-stream',
      'X-Tenant-Slug': tenantSlug,
    };

    const token = this.getAuthToken();

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    void fetchEventSource(this.buildStreamUrl(), {
      method: 'GET',
      headers,
      credentials: 'include',
      openWhenHidden: true,
      onopen: async (response: Response) => {
        const contentType = response.headers.get('content-type') ?? '';

        if (!response.ok) {
          throw new Error(`Falha ao abrir stream SSE: ${response.status}`);
        }

        if (!contentType.includes('text/event-stream')) {
          throw new Error(
            `Resposta invalida para SSE. Content-Type recebido: ${contentType || 'vazio'}`
          );
        }

        this.notificationStore.setConnectionState('open');
      },
      onmessage: (event: EventSourceMessage) => {
        const payload = this.parseEvent(event);

        if (!payload) {
          return;
        }

        this.notificationStore.setConnectionState('open');
        this.notificationStore.setLastEventAt(payload.emittedAt);

        if (payload.type !== 'order.created') {
          return;
        }

        const notification = this.notificationRulesService.createFromOrderCreated(
          payload,
          this.notificationSettingsService.snapshot()
        );

        if (!notification) {
          return;
        }

        this.notificationStore.add(notification);
        this.snackbarService.show(
          this.notificationRulesService.toSnackbar(notification)
        );
      },
      onclose: () => {
        this.notificationStore.setConnectionState('connecting');
        throw new Error('notification-stream-closed');
      },
      onerror: (error: unknown) => {
        if (this.isAbortError(error)) {
          return;
        }

        this.notificationStore.setConnectionState('error');
        return 2000;
      },
    });
  }

  private parseEvent(
    event: EventSourceMessage
  ): NotificationRealtimeEvent | null {
    if (!event.data) {
      return null;
    }

    try {
      return JSON.parse(event.data) as NotificationRealtimeEvent;
    } catch {
      return null;
    }
  }

  private buildStreamUrl(): string {
    return `${this.cfg.apiBaseUrl.replace(/\/$/, '')}/orders/stream`;
  }

  private getAuthToken(): string {
    const candidates = [
      sessionStorage.getItem(this.cfg.authStorageKey),
      sessionStorage.getItem('accessToken'),
      sessionStorage.getItem('authToken'),
      sessionStorage.getItem('jwt'),
      localStorage.getItem(this.cfg.authStorageKey),
      localStorage.getItem('accessToken'),
      localStorage.getItem('authToken'),
      localStorage.getItem('jwt'),
    ];

    return candidates.find((value) => !!value?.trim())?.trim() ?? '';
  }

  private resolveTenantSlug(): string {
    const configuredTenant = this.cfg.tenantSlug?.trim();

    if (configuredTenant) {
      return configuredTenant;
    }

    const storageCandidates = [
      sessionStorage.getItem('tenantSlug'),
      sessionStorage.getItem('tenant_slug'),
      localStorage.getItem('tenantSlug'),
      localStorage.getItem('tenant_slug'),
    ];
    const storageSlug =
      storageCandidates.find((value) => !!value?.trim())?.trim() ?? '';

    if (storageSlug) {
      return storageSlug;
    }

    return this.extractTenantSlugFromToken(this.getAuthToken());
  }

  private extractTenantSlugFromToken(token: string): string {
    if (!token) {
      return '';
    }

    try {
      const [, payload] = token.split('.');

      if (!payload) {
        return '';
      }

      const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
      const padded = normalized.padEnd(
        normalized.length + ((4 - (normalized.length % 4)) % 4),
        '='
      );
      const decoded = atob(padded);
      const parsed = JSON.parse(decoded) as { tslug?: string };

      return parsed.tslug?.trim() ?? '';
    } catch {
      return '';
    }
  }

  private isAbortError(error: unknown): boolean {
    if (error instanceof DOMException) {
      return error.name === 'AbortError';
    }

    return (
      typeof error === 'object' &&
      error !== null &&
      'name' in error &&
      error.name === 'AbortError'
    );
  }
}
